import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export function SuggestYoutuberDialog({ trigger }: { trigger: ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    suggester_name: "",
    suggester_email: "",
    youtuber_name: "",
    channel_url: "",
    notes: "",
  });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.youtuber_name.trim() || !form.channel_url.trim()) return;
    setSending(true);
    const { error } = await supabase.from("youtuber_suggestions").insert({
      suggester_name: form.suggester_name.trim() || null,
      suggester_email: form.suggester_email.trim() || null,
      youtuber_name: form.youtuber_name.trim(),
      channel_url: form.channel_url.trim(),
      notes: form.notes.trim() || null,
    });
    setSending(false);
    if (error) {
      toast.error(t("home.suggest.error"));
      return;
    }
    toast.success(t("home.suggest.success"));
    setForm({ suggester_name: "", suggester_email: "", youtuber_name: "", channel_url: "", notes: "" });
    setOpen(false);
  }

  const inputCls =
    "w-full bg-paper ring-hairline px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-amber/50";
  const labelCls = "text-mono-caps text-[10px] text-ink/50 mb-1 block";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-paper max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-lg">{t("home.suggest.title")}</DialogTitle>
          <DialogDescription className="text-sm text-ink/60">
            {t("home.suggest.desc")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className={labelCls}>{t("home.suggest.youtuberName")} *</label>
            <input required value={form.youtuber_name} onChange={set("youtuber_name")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t("home.suggest.channel")} *</label>
            <input required value={form.channel_url} onChange={set("channel_url")} className={inputCls} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>
                {t("home.suggest.yourName")} <span className="lowercase">({t("home.suggest.optional")})</span>
              </label>
              <input value={form.suggester_name} onChange={set("suggester_name")} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>
                {t("home.suggest.yourEmail")} <span className="lowercase">({t("home.suggest.optional")})</span>
              </label>
              <input type="email" value={form.suggester_email} onChange={set("suggester_email")} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>
              {t("home.suggest.notes")} <span className="lowercase">({t("home.suggest.optional")})</span>
            </label>
            <textarea rows={3} value={form.notes} onChange={set("notes")} className={inputCls} />
          </div>
          <button
            type="submit"
            disabled={sending}
            className="w-full bg-ink text-paper text-sm font-medium px-3.5 py-2 hover:bg-amber transition-colors disabled:opacity-50"
          >
            {sending ? t("home.suggest.sending") : t("home.suggest.submit")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
